import { inject, Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { map, Observable, of, take } from "rxjs";
import { Role } from "../dtos/user.dto";
import { SessionService } from "./session.service";

@Injectable({
  providedIn: "root",
})
export class PermissionChecker {
  private readonly session = inject(SessionService);
  private readonly route = inject(ActivatedRoute);

  currentRole(businessId?: string): Role | undefined {
    const id = businessId ?? this.resolveBusinessId();
    const user = this.session.currentUser();
    if (!user || !id) return undefined;
    return user.businessUsers?.find((bu) => bu.businessId === id || bu.business?.id === id)?.role;
  }

  isAccountManager(businessId?: string): boolean {
    return this.currentRole(businessId) === Role.ACCOUNT_MANAGER;
  }

  isProjectManager(businessId?: string): boolean {
    return this.currentRole(businessId) === Role.PROJECT_MANAGER;
  }

  hasRole(role: string | string[], businessId?: string): Observable<boolean> {
    const allowedRoles = Array.isArray(role) ? role : [role];
    const id = businessId ?? this.resolveBusinessId();

    const roleFromUser = (user: { businessUsers?: { businessId: string; business?: { id?: string }; role: Role }[] } | null): boolean => {
      // If user or businessId is not loaded yet, allow access (will be checked later).
      if (!user || !id) {
        return true;
      }
      const currentRole = user.businessUsers?.find((bu) => bu.businessId === id || bu.business?.id === id)?.role;
      return !!currentRole && allowedRoles.includes(currentRole);
    };

    const existing = this.session.currentUser();
    if (existing) {
      return of(roleFromUser(existing));
    }

    return this.session.loadCurrentUser().pipe(take(1), map((user) => roleFromUser(user)));
  }

  private resolveBusinessId(): string | null {
    let route: ActivatedRoute | null = this.route;
    while (route) {
      const id = route.snapshot.paramMap.get("businessId");
      if (id) return id;
      route = route.parent;
    }
    return null;
  }
}
